/**
 * MessageThread Component
 *
 * Full conversation view with messages and input.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useThreadMessages, useSendMessage, type MessageThread as MessageThreadType, type Message } from '../hooks';
import { MessageInput } from './MessageInput';
import { QuickReplies } from './QuickReplies';
import { GuestSidebar } from './GuestSidebar';

interface MessageThreadProps {
  thread: MessageThreadType;
}

export const MessageThread: React.FC<MessageThreadProps> = ({ thread }) => {
  const { messages, isLoading } = useThreadMessages(thread.id);
  const { sendMessage, markAsRead, isSending } = useSendMessage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Lifted message-text state so QuickReplies can pre-fill the input.
  const [draft, setDraft] = useState('');

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark as read when viewing
  useEffect(() => {
    if (thread.unreadCount > 0) {
      markAsRead(thread.id);
    }
  }, [thread.id, thread.unreadCount, markAsRead]);

  const handleSend = async (content: string) => {
    const ok = await sendMessage({
      threadId: thread.id,
      content,
    });
    if (ok) setDraft('');
  };

  const handleQuickReply = (content: string) => {
    setDraft(content);
  };

  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday =
      new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const dateKey = message.createdAt.toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
    return groups;
  }, {} as Record<string, Message[]>);

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday =
      new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="flex-1 flex">
      {/* Messages Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center gap-3">
            <img
              src={thread.guestAvatar}
              alt={thread.guestName}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{thread.guestName}</h3>
              <p className="text-sm text-gray-500">{thread.listingTitle}</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              No messages yet. Start the conversation!
            </div>
          ) : (
            Object.entries(groupedMessages).map(([dateKey, dateMessages]) => (
              <div key={dateKey}>
                {/* Date Header */}
                <div className="flex items-center justify-center my-4">
                  <span className="px-3 py-1 bg-white rounded-full text-xs text-gray-500 shadow-sm">
                    {formatDateHeader(dateKey)}
                  </span>
                </div>

                {/* Messages for this date */}
                {dateMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex mb-4 ${
                      message.senderType === 'host' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] ${
                        message.senderType === 'host'
                          ? 'bg-primary-500 text-white rounded-2xl rounded-br-sm'
                          : 'bg-white text-gray-900 rounded-2xl rounded-bl-sm shadow-sm'
                      } px-4 py-3`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.senderType === 'host' ? 'text-primary-100' : 'text-gray-400'
                        }`}
                      >
                        {formatMessageTime(message.createdAt)}
                        {message.senderType === 'host' && message.isRead && (
                          <span className="ml-2">✓✓</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        <QuickReplies
          onSelect={handleQuickReply}
          guestName={thread.guestName}
          listingTitle={thread.listingTitle}
        />

        {/* Input */}
        <MessageInput
          onSend={handleSend}
          isSending={isSending}
          placeholder={`Message ${thread.guestName}...`}
          value={draft}
          onChange={setDraft}
        />
      </div>

      {/* Guest Sidebar */}
      <GuestSidebar
        guest={{
          id: thread.guestId,
          name: thread.guestName,
          avatar: thread.guestAvatar,
          memberSince: '2023',
          reviewCount: 5,
        }}
        listing={{
          id: thread.listingId,
          title: thread.listingTitle,
          image: thread.listingImage,
        }}
        booking={
          thread.bookingId
            ? {
                id: thread.bookingId,
                status: thread.bookingStatus || 'pending',
                checkIn: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                checkOut: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
                guests: 2,
                totalPrice: 450,
                hostPayout: 420,
              }
            : undefined
        }
      />
    </div>
  );
};

export default MessageThread;
